// Coach marks: a short tour that points at the real UI, one step at a time (a ring round the thing, a small card
// next to it with 下一步 / 跳过), and one-off hints timed to something happening (OtterBonk's first pearl). The content
// (which tours, which steps) is public/tours.js; this is only the machinery.
//   defineTour('spy', { name: '谁是卧底', steps: [{ at: '#sfull .smyword', text: '…' }, …] });
//   autoTour('spy')              -> the first time only (seen once per person: here and, through the office, their account)
//   startTour('spy')             -> again (the 玩法 buttons, tourButton())
//   hint('ob-pearl', { at: () => ({ x, y }), text: '…' })   -> a one-step card that goes by itself, once per person
// A step: at = a selector (or several, the first one on screen), an element, or a function giving one of those or a
// screen point {x, y, w?, h?}; round (a circle); text; place 'auto' (default) | 'top' | 'bottom' | 'left' | 'right'; skip() to leave it
// out; title, text, btn and the tour's name / skip may be functions (ctx) => text, asked again on a language change; enter() / leave() around it; wait() (a step the player has to do: no 下一步, it goes on once wait() is true);
// btn (the 下一步 button's text); dim (this step's, else the tour's). Nothing under the tour is blocked: only the card takes clicks (the dimming is a
// shadow with pointer-events: none). Enter is 下一步 and Esc is 跳过 while a tour card is up (not while typing).
// Unversioned on purpose (like icons.js): the page and the games import it by the same URL and share one instance.
import { icon } from '/icons.js';
import { t, tr, onLang } from '/i18n.js';

const KEY = 'deck.tours';
const seen = new Set((() => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } })());
const save = () => { try { localStorage.setItem(KEY, JSON.stringify([...seen])); } catch {} };
const RESET = 'deck.tours.reset'; // when this device last took a reset (ms), from here or from the office
let resetAt = (() => { try { return Number(localStorage.getItem(RESET)) || 0; } catch { return 0; } })();
let upload = null; // (list, reset?) => void: the office keeps the list against my Raft account (tourStore)
// Saving to the office: at most one message every 2 s, the latest list (a few tours seen in a row make one save).
let upTimer = 0, upLast = 0;
function push() {
  if (!upload || upTimer) return;
  upTimer = setTimeout(() => { upTimer = 0; upLast = Date.now(); upload([...seen]); }, Math.max(0, upLast + 2000 - Date.now()));
}
const defs = new Map();
let cur = null; // { id, def, steps, i, hint, timer }
const queue = [];

/** Has this person seen tour (or hint) `id`? */
export const tourSeen = (id) => seen.has(id);
/** Count `id` as seen (kept here and on my account, as a tour shown is): e.g. OtterBonk's 新手教程, once done or skipped. */
export function markSeen(id) { if (seen.has(id)) return; seen.add(id); save(); push(); }
/** The office's copy of what I've seen (its "tours" message): merged with this device's, and anything only seen here
 *  sent back up. `send(list, reset)` keeps the list on my account from now on. `reset`: when 重新显示 was last pressed
 *  (on any device): this device's list from before then is dropped first, so the reset sticks. */
export function tourStore(list, send, reset = 0) {
  upload = send;
  if (reset > resetAt) { resetAt = reset; seen.clear(); try { localStorage.setItem(RESET, String(reset)); } catch {} save(); }
  const before = seen.size;
  for (const id of Array.isArray(list) ? list : []) if (typeof id === 'string') seen.add(id);
  if (seen.size !== before) save();
  if (seen.size !== (Array.isArray(list) ? list.length : 0)) push();
}
/** Forget every tour and hint seen (设置 / 新手引导 / 重新显示所有游戏教程). */
export function resetTours() {
  seen.clear(); save(); clearTimeout(upTimer); upTimer = 0;
  resetAt = Date.now(); try { localStorage.setItem(RESET, String(resetAt)); } catch {}
  upload?.([], true);
}

/** Register a tour: { name, steps: [...] | (ctx) => [...], dim (0..1, default .5), alive: () => bool (it closes once
 *  false: its screen went), ready: () => bool (an auto tour waits for it: the office's, until back on the map),
 *  hidden: () => bool (the card and ring stay out of sight meanwhile, e.g. under 请横过来玩), skip (跳过's text),
 *  esc: false (Esc stays the game's: only 跳过 ends it),
 *  onEnd(done) (it closed: done after its last step, else 跳过 / Esc / its screen gone) }. */
export function defineTour(id, def) { defs.set(id, def); }
export const hasTour = (id) => defs.has(id);

/** Start tour `id` now (a replay), over whatever tour is up. */
export function startTour(id, ctx = {}) {
  const def = defs.get(id); if (!def) return false;
  if (cur) end();
  queue.length = 0;
  run({ id, def, ctx });
  return true;
}
/** Start tour `id` if this person has never seen it (after `delay` ms, and once nothing covers the screen: the loading
 *  screen, another tour). Cheap to call on every render. */
export function autoTour(id, ctx = {}, { delay = 500 } = {}) {
  if (seen.has(id) || !defs.has(id) || cur?.id === id || queue.some((q) => q.id === id)) return false;
  queue.push({ id, def: defs.get(id), ctx, at: performance.now() + delay });
  pump();
  return true;
}
/** A one-step card that goes by itself after `ms` (and with its 知道了), once per person; it waits behind a tour.
 *  `valid()`: still worth saying (checked when its turn comes, and while it's up: it goes once that's false). `prio`:
 *  the higher goes first when several wait (OtterBonk's upgrade cards before its pearl). */
export function hint(id, { at, text, title, place, ms = 7000, valid = () => true, alive, prio = 0 } = {}) {
  if (seen.has(id) || cur?.id === id || queue.some((q) => q.id === id)) return false;
  queue.push({ id, hint: true, def: { name: title ?? (() => t('tour.hint')), steps: [{ at, text, place }], dim: 0, alive }, valid, ms, prio, at: performance.now() });
  pump();
  return true;
}
/** A 「玩法」 button that replays tour `id` (or `id()`, for a panel whose game changes). */
export function tourButton(id, { text, cls = '', ctx } = {}) {
  const b = document.createElement('button'); b.type = 'button'; b.className = ('tourbtn ' + cls).trim();
  // (the default words: a <span> that follows a language change by itself; the title too)
  b.append(icon('bulb'), text != null ? document.createTextNode(text) : tr(document.createElement('span'), 'tour.button')); tr(b, null, null, { title: 'tour.buttonTip' });
  b.onclick = (e) => { e.stopPropagation(); startTour(typeof id === 'function' ? id() : id, typeof ctx === 'function' ? ctx() : ctx); };
  return b;
}
/** Is any element matching `sel` shown (not display:none, itself or above)? For tours' alive() / ready(). */
export const shown = (sel) => [...document.querySelectorAll(sel)].some((e) => (e.checkVisibility ? e.checkVisibility() : e.getClientRects().length > 0));
/** Is a tour card up right now? */
export const touring = () => !!cur;

// ---- the queue: one at a time, not over the loading screen
// the map editor (deck/map-editor.js) is a tool, not a screen to tour: nothing starts while it's open, and a tour
// already up steps out of sight until it closes (its card would sit on the editor's 保存并同步 on a phone)
const editing = () => shown('#map-editor');
const covered = () => editing() || [...document.querySelectorAll('.ldg')].some((e) => getComputedStyle(e).display !== 'none' && getComputedStyle(e).opacity !== '0');
let pumpTimer = 0;
function pump() {
  clearTimeout(pumpTimer);
  if (cur || !queue.length) return;
  const now = performance.now();
  // drop what no longer applies (a hint whose moment passed, a tour whose screen closed)
  for (let i = queue.length - 1; i >= 0; i--) { const q = queue[i]; if (seen.has(q.id) || (q.def.alive && !q.def.alive()) || (q.hint && !q.valid())) queue.splice(i, 1); }
  // a tour first: hints wait while one is due and can show now (OtterBonk's HUD tour before its first-enemy hint);
  // a tour still waiting for its screen (the office tour during a first OtterBonk run) doesn't hold them back
  const can = (x) => x.at <= now && (x.def.ready?.() ?? true);
  const ready = queue.filter((x) => can(x) && (!x.hint || !queue.some((y) => !y.hint && can(y))));
  const q = ready.sort((a, b) => (b.prio ?? 0) - (a.prio ?? 0))[0];
  if (!q || covered() || document.hidden) { if (queue.length) pumpTimer = setTimeout(pump, 300); return; }
  queue.splice(queue.indexOf(q), 1);
  run(q);
}

// ---- showing it
const CSS = `#tour{position:fixed;inset:0;z-index:95;pointer-events:none}
#tour .tspot{position:fixed;border:3px solid #FDD03B;border-radius:12px;box-shadow:0 0 0 3px #1b1410,0 0 0 200vmax rgba(15,30,45,var(--tdim,.5));pointer-events:none;transition:left .18s,top .18s,width .18s,height .18s;box-sizing:border-box}
#tour .tspot.ring{animation:tpulse 1.2s steps(3) infinite}
@keyframes tpulse{50%{border-color:#fff8e7}}
#tour .tcard{position:fixed;pointer-events:auto;width:max-content;max-width:min(300px,calc(100vw - 20px));background:#fff8e7;border:3px solid #1b1410;border-radius:12px;box-shadow:4px 4px 0 #1b1410;padding:10px 12px;box-sizing:border-box;font:14px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;text-align:left}
#tour .thead{display:flex;gap:6px;align-items:center;font-weight:700;margin-bottom:2px}
#tour .tcount{margin-left:auto;padding-left:10px;font-size:12px;font-weight:400;color:#8a6a3a;font-variant-numeric:tabular-nums}
#tour .ttext{white-space:pre-line}
#tour .tbtns{display:flex;gap:8px;justify-content:flex-end;margin-top:8px}
#tour .tbtns button{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:3px 14px;background:#fff;cursor:pointer;color:#1b1410}
#tour .tbtns .tnext{background:#FDD03B;font-weight:600}
#tour .tarrow{position:absolute;width:10px;height:10px;background:#fff8e7;border:3px solid #1b1410;transform:rotate(45deg)}
#tour .tarrow.b{top:-8px;border-right:0;border-bottom:0}
#tour .tarrow.t{bottom:-8px;border-left:0;border-top:0}
#tour .tarrow.r{left:-8px;border-top:0;border-right:0}
#tour .tarrow.l{right:-8px;border-bottom:0;border-left:0}
@media (pointer:coarse){#tour .tbtns button{padding:6px 16px;min-height:44px}}
.tourbtn{display:inline-flex;align-items:center;gap:4px;font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:2px 10px;background:#fff;cursor:pointer;white-space:nowrap;color:#1b1410}
.tourbtn.small{font-size:12px;padding:1px 8px;gap:2px}`;
let rootEl = null, spot, card, arrow, head, count, textEl, skipBtn, nextBtn;
{ const s = document.createElement('style'); s.id = 'tour-css'; s.textContent = CSS; document.head.appendChild(s); } // now: the 玩法 buttons use it too
function build() {
  if (rootEl) return;
  const mk = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
  rootEl = mk('div'); rootEl.id = 'tour'; rootEl.style.display = 'none';
  spot = mk('div', 'tspot'); card = mk('div', 'tcard'); arrow = mk('i', 'tarrow');
  card.setAttribute('role', 'dialog'); card.setAttribute('aria-live', 'polite');
  head = mk('div', 'thead'); count = mk('span', 'tcount'); textEl = mk('div', 'ttext');
  const btns = mk('div', 'tbtns');
  skipBtn = mk('button', 'tskip', t('tour.skip')); skipBtn.type = 'button'; skipBtn.title = 'Esc';
  nextBtn = mk('button', 'tnext', t('tour.next')); nextBtn.type = 'button'; nextBtn.title = 'Enter';
  skipBtn.onclick = (e) => { e.stopPropagation(); end(); };
  nextBtn.onclick = (e) => { e.stopPropagation(); step(1); };
  // the card takes its own pointer events: nothing under it (the map, a game's canvas) sees them
  for (const ev of ['pointerdown', 'mousedown', 'touchstart', 'click', 'wheel']) card.addEventListener(ev, (e) => e.stopPropagation());
  btns.append(skipBtn, nextBtn); card.append(arrow, head, textEl, btns); rootEl.append(spot, card);
  document.body.appendChild(rootEl);
}

function run(q) {
  const steps = (typeof q.def.steps === 'function' ? q.def.steps(q.ctx) : q.def.steps).filter((s) => !s.skip?.(q.ctx));
  if (!steps.length) return pump();
  build();
  markSeen(q.id); // seen once shown: a reload halfway doesn't bring it back
  cur = { ...q, steps, i: -1, last: '' };
  rootEl.style.display = ''; rootEl.style.visibility = '';
  rootEl.style.setProperty('--tdim', String(q.def.dim ?? 0.5));
  step(1);
  loop();
}
function step(d) {
  if (!cur) return;
  cur.steps[cur.i]?.leave?.(cur.ctx);
  cur.i += d;
  if (cur.i >= cur.steps.length) return end();
  const s = cur.steps[cur.i];
  s.enter?.(cur.ctx);
  // a button of the bar scrolled out of sight (a phone: one row that scrolls sideways, index.html) comes to its middle
  const at = typeof s.at === 'string' ? document.querySelector(s.at) : null, bar = at?.closest?.('.bar');
  if (bar && bar.scrollWidth > bar.clientWidth) {
    const a = at.getBoundingClientRect(), b = bar.getBoundingClientRect();
    bar.scrollLeft += a.left + a.width / 2 - (b.left + b.width / 2);
  }
  draw();
  const dim = s.dim ?? cur.def.dim ?? 0.5;
  rootEl.style.setProperty('--tdim', String(dim)); spot.classList.toggle('ring', !!cur.hint || dim < 0.3);
  cur.last = ''; cur.prev = null;
  clearTimeout(cur.timer);
  if (cur.hint) cur.timer = setTimeout(end, cur.ms);
  layout();
}
const val = (x) => (typeof x === 'function' ? x(cur.ctx) : x);
// the card's words for the current step (again after a language change)
function draw() {
  const s = cur.steps[cur.i], n = cur.steps.length;
  head.replaceChildren(icon('bulb'), document.createTextNode(val(s.title) ?? val(cur.def.name) ?? t('tour.title')));
  count.textContent = cur.hint || n < 2 ? '' : `${cur.i + 1}/${n}`; head.append(count);
  textEl.textContent = val(s.text);
  skipBtn.style.display = cur.hint || cur.i === n - 1 ? 'none' : '';
  skipBtn.textContent = val(cur.def.skip) ?? t('tour.skip');
  nextBtn.textContent = val(s.btn) ?? t(cur.hint || cur.i === n - 1 ? 'tour.done' : 'tour.next');
  nextBtn.style.display = s.wait && !s.btn ? 'none' : ''; // the player does it: no 下一步
}
onLang(() => { if (cur && cur.steps[cur.i]) { draw(); cur.last = ''; layout(); } });
function end() {
  if (!cur) return;
  const was = cur, done = cur.i >= cur.steps.length;
  cur.steps[cur.i]?.leave?.(cur.ctx);
  clearTimeout(cur.timer);
  cur = null;
  rootEl.style.display = 'none';
  pumpTimer = setTimeout(pump, 400);
  was.def.onEnd?.(done);
}
function loop() {
  if (!cur) return;
  if (cur.steps[cur.i]?.wait?.(cur.ctx)) step(1);
  if (cur) layout();
  if (cur) requestAnimationFrame(loop);
}

/** Where step `s` points on screen now: a DOMRect-like {left, top, width, height}, or null. */
function target(s, ctx) {
  let v = typeof s.at === 'function' ? s.at(ctx) : s.at;
  if (Array.isArray(v)) v = v.map((x) => document.querySelector(x)).find(visible) ?? null;
  else if (typeof v === 'string') v = document.querySelector(v);
  if (!v) return null;
  if (v instanceof Element) {
    if (!visible(v)) return null;
    const r = v.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  }
  if (Number.isFinite(v.x) && Number.isFinite(v.y)) { const w = v.w ?? 56, h = v.h ?? 56; return { left: v.x - w / 2, top: v.y - h / 2, width: w, height: h }; }
  return null;
}
function visible(e) {
  if (!e?.isConnected) return false;
  if (e.checkVisibility && !e.checkVisibility({ visibilityProperty: true })) return false;
  const r = e.getBoundingClientRect();
  return r.width > 1 && r.height > 1 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
}
function layout() {
  if (!cur) return;
  if ((cur.def.alive && !cur.def.alive()) || (cur.hint && !cur.valid())) return end();
  const hide = !!cur.def.hidden?.() || editing();
  if (hide !== (rootEl.style.visibility === 'hidden')) { rootEl.style.visibility = hide ? 'hidden' : ''; cur.last = ''; }
  if (hide) return;
  const s = cur.steps[cur.i], vw = innerWidth, vh = innerHeight, M = 8, PAD = s.pad ?? 6;
  let r = target(s, cur.ctx);
  // a target that blinks out (an enemy that just died, a panel redrawn) keeps its ring where it was for a moment
  if (r) { cur.prev = r; cur.prevAt = performance.now(); } else if (cur.prev && performance.now() - cur.prevAt < 1500) r = cur.prev;
  if (r && s.round) r = { left: r.left - PAD, top: r.top - PAD, width: r.width + 2 * PAD, height: r.height + 2 * PAD }; // a circle stays round, past the edges if need be
  else if (r) { // the ring: a little bigger than the thing, kept on screen
    const x0 = Math.max(M / 2, r.left - PAD), y0 = Math.max(M / 2, r.top - PAD), x1 = Math.min(vw - M / 2, r.left + r.width + PAD), y1 = Math.min(vh - M / 2, r.top + r.height + PAD);
    r = { left: x0, top: y0, width: Math.max(0, x1 - x0), height: Math.max(0, y1 - y0) };
  }
  const key = r ? `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)},${vw},${vh}` : `none,${vw},${vh}`;
  if (key === cur.last) return;
  cur.last = key;
  spot.style.display = r ? '' : 'none';
  spot.style.borderRadius = s.round ? '50%' : '';
  if (r) Object.assign(spot.style, { left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px' });
  // the card: beside the ring where it fits (preferred side first), else over the bottom of the screen
  const cw = card.offsetWidth, ch = card.offsetHeight, G = 14;
  let x = (vw - cw) / 2, y = vh - ch - Math.max(M, vh * 0.12), side = null;
  if (r) {
    const fits = {
      bottom: r.top + r.height + G + ch <= vh - M, top: r.top - G - ch >= M,
      right: r.left + r.width + G + cw <= vw - M, left: r.left - G - cw >= M,
    };
    const order = [s.place, 'bottom', 'top', 'right', 'left'].filter((p, i, a) => p && p !== 'auto' && a.indexOf(p) === i);
    side = order.find((p) => fits[p]) ?? null;
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    if (side === 'bottom') { x = cx - cw / 2; y = r.top + r.height + G; }
    else if (side === 'top') { x = cx - cw / 2; y = r.top - G - ch; }
    else if (side === 'right') { x = r.left + r.width + G; y = cy - ch / 2; }
    else if (side === 'left') { x = r.left - G - cw; y = cy - ch / 2; }
    else { y = r.top + r.height / 2 > vh / 2 ? M + Math.min(60, vh * 0.08) : vh - ch - M - Math.min(60, vh * 0.08); } // too big to sit beside: the half it covers least
  }
  x = Math.max(M, Math.min(vw - cw - M, x)); y = Math.max(M, Math.min(vh - ch - M, y));
  card.style.left = Math.round(x) + 'px'; card.style.top = Math.round(y) + 'px';
  arrow.style.display = side ? '' : 'none';
  if (side) {
    arrow.className = 'tarrow ' + side[0];
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    if (side === 'bottom' || side === 'top') { arrow.style.left = Math.max(10, Math.min(cw - 24, cx - x - 8)) + 'px'; arrow.style.top = ''; }
    else { arrow.style.top = Math.max(10, Math.min(ch - 24, cy - y - 8)) + 'px'; arrow.style.left = ''; }
  }
}
addEventListener('resize', () => { if (cur) { cur.last = ''; layout(); } });

// Enter: 下一步, Esc: 跳过, while a tour card is up (a hint leaves the keys alone: the game behind may want them).
// Window, capture phase, registered when this module loads: ahead of the map's and the games' own key handlers.
addEventListener('keydown', (e) => {
  if (!cur || cur.hint || e.isComposing || e.keyCode === 229 || rootEl.style.visibility === 'hidden') return;
  if (e.repeat && (e.key === 'Enter' || e.key === 'Escape')) { e.preventDefault(); e.stopImmediatePropagation(); return; } // Enter held down doesn't race through the steps
  if (e.target?.closest?.('input, textarea, select, [contenteditable="true"], [contenteditable=""], .dd-list')) return; // .dd-list: an open dropdown (public/dropdown.js)
  if (e.key === 'Enter') { if (nextBtn.style.display === 'none') return; step(1); } // (a step to do: Enter doesn't skip it)
  else if (e.key === 'Escape') { if (cur.def.esc === false) return; end(); } // (a tour over a game whose Esc is its own: 跳过 only)
  else return;
  e.preventDefault(); e.stopImmediatePropagation();
}, true);
